import React, { useEffect, useState, useRef } from 'react';
import { BookOpenIcon, PlayIcon, TrendingUpIcon, CheckIcon, CalendarIcon, SearchIcon, XIcon, BarChartIcon, FlameIcon, SmartphoneIcon, BeakerIcon, FilterIcon, ChevronDownIcon, ArrowUpIcon } from 'lucide-react';
import { Tooltip } from './Charts';
import { books, advancedMetrics } from './constants';
import { OverviewTab } from './OverviewTab';
import { EngagementTab } from './EngagementTab';
import { DevicesTab } from './DevicesTab';
import { AdvancedTab } from './AdvancedTab';
import type { Book } from './types';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
const Analytics = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  // Referenced by hover-state ternaries below but was never declared.
  const darkMode = dk.isDark;
  const [selectedBook, setSelectedBook] = useState<Book>(books[0]);
  const [selectedTimeframe, setSelectedTimeframe] = useState('30days');
  const [showTimeframeDropdown, setShowTimeframeDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredBooks, setFilteredBooks] = useState(books);
  const [viewMode, setViewMode] = useState('individual');
  const [selectedAdvancedMetric, setSelectedAdvancedMetric] = useState(advancedMetrics[0].id);
  const bookSearchRef = useRef<HTMLDivElement>(null);
  const timeframeDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredBooks(books);
    } else {
      const filtered = books.filter(book => book.title.toLowerCase().includes(searchTerm.toLowerCase()) || book.author.toLowerCase().includes(searchTerm.toLowerCase()));
      setFilteredBooks(filtered);
    }
  }, [searchTerm]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (searchTerm && filteredBooks.length > 0 && bookSearchRef.current && !bookSearchRef.current.contains(target)) {
        setSearchTerm('');
      }
      if (showTimeframeDropdown && timeframeDropdownRef.current && !timeframeDropdownRef.current.contains(target)) {
        setShowTimeframeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchTerm, filteredBooks.length, showTimeframeDropdown]);
  const totalStats = {
    downloads: books.reduce((sum, book) => sum + book.downloads, 0),
    startRate: Math.round(books.reduce((sum, book) => sum + book.startRate, 0) / books.length),
    completionRate: Math.round(books.reduce((sum, book) => sum + book.completionRate, 0) / books.length),
    averageReadDepth: Math.round(books.reduce((sum, book) => sum + book.averageReadDepth, 0) / books.length)
  };
  return <div className={`min-h-screen ${dk.page}`} data-id="element-5396" data-mp="yrbcwxbmcvu6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-5397" data-mp="ysmgaw15k1kr">
        {/* Header */}
        <div className="mb-8" data-id="element-5398" data-mp="1dh8uguf1mnae">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-5399" data-mp="18oz9ztwp6ffi">
            <div data-id="element-5400" data-mp="18n4n4a1v8d8s">
              <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-5401" data-mp="1mg428axqk5ft">
                Author Analytics
              </h1>
              <p className={`mt-1 ${dk.textSecondary}`} data-id="element-5402" data-mp="1hi2hzmokq02r">
                Understand how readers engage with your books
              </p>
            </div>
            <div className="mt-4 md:mt-0" data-id="element-5403" data-mp="1wch2xsrp7jk2">
              <button onClick={() => setViewMode(viewMode === 'all' ? 'individual' : 'all')} className={`px-4 py-2 rounded-md transition-colors text-white ${rt.primaryButton}`} data-id="element-5404" data-mp="1rf7a0353oprh">
                {viewMode === 'all' ? 'View Individual Books' : 'View All Books'}
              </button>
            </div>
          </div>
        </div>

        {/* Book Selection & Timeframe */}
        <div className={`rounded-lg shadow-sm p-4 mb-6 border ${dk.card}`} data-id="element-5405" data-mp="2u7jj1r8xxgnb">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-5406" data-mp="2y9o78agmdl2k">
            {/* Book Search */}
            {viewMode === 'individual' && <div className="relative w-full md:w-64" ref={bookSearchRef} data-id="element-5407" data-mp="21l8g49xv33nc">
                <div className="relative" data-id="element-5408" data-mp="26j9zyx3436zv">
                  <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${dk.textFaint}`} data-id="element-5409" data-mp="26g4dlplvaj7u" />
                  <input type="text" placeholder="Search books" className={`w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.inputOnCard}`} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-5410" data-mp="2fbu061z1xjmt" />
                  {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2" data-id="element-5411" data-mp="1ad6uokelq0i4">
                      <XIcon className={`h-4 w-4 ${dk.textFaint}`} data-id="element-5412" data-mp="11a5suvwrqvk9" />
                    </button>}
                </div>
                {searchTerm && filteredBooks.length > 0 && <div className={`absolute z-10 mt-1 w-full shadow-lg rounded-md py-1 text-sm ring-1 ring-black ring-opacity-5 border ${dk.card}`} data-id="element-5413" data-mp="rkcrenqcbwtq">
                    {filteredBooks.map(book => <button key={book.id} className={`flex items-center w-full px-4 py-2 text-sm text-left ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`} onClick={() => {
                setSelectedBook(book);
                setSearchTerm('');
              }} data-id="element-5414" data-mp="iimskwu4pvlx">
                        <div className="w-8 h-12 mr-3 flex-shrink-0" data-id="element-5415" data-mp="3vxvf1ncnuip">
                          <img src={book.coverImage} alt={book.title} className="w-full h-full object-cover rounded" data-id="element-5416" data-mp="3l7p36xgu45eu" />
                        </div>
                        <div data-id="element-5417" data-mp="mo8o62oaic33">
                          <span className={`font-medium ${dk.textPrimary}`} data-id="element-5418" data-mp="mn72z5dk897n">
                            {book.title}
                          </span>
                          <p className={`text-xs ${dk.textMuted}`} data-id="element-5419" data-mp="cyp7n3o1uube">
                            by {book.author}
                          </p>
                        </div>
                      </button>)}
                  </div>}
              </div>}

            {/* Selected Book Display */}
            {viewMode === 'individual' && !searchTerm && <div className="flex items-center" data-id="element-5420" data-mp="2l6bujgm3s080">
                <div className="w-10 h-14 mr-3 flex-shrink-0" data-id="element-5421" data-mp="2l5iypaoomv4n">
                  <img src={selectedBook.coverImage} alt={selectedBook.title} className="w-full h-full object-cover rounded" data-id="element-5422" data-mp="2zvq68vd5itqk" />
                </div>
                <div data-id="element-5423" data-mp="277m24ea8aopz">
                  <h3 className={`font-medium ${dk.textPrimary}`} data-id="element-5424" data-mp="276t1lv1v4vga">
                    {selectedBook.title}
                  </h3>
                  <p className={`text-sm ${dk.textMuted}`} data-id="element-5425" data-mp="2g793j02yjuj1">
                    by {selectedBook.author}
                  </p>
                </div>
              </div>}

            {/* Timeframe Selector */}
            <div className="relative" ref={timeframeDropdownRef} data-id="element-5426" data-mp="3rluxndzbwh1a">
              <button onClick={() => setShowTimeframeDropdown(!showTimeframeDropdown)} className={`flex items-center justify-between w-full md:w-48 px-4 py-2 border rounded-md shadow-sm text-sm font-medium ${dk.card}`} data-id="element-5427" data-mp="3ro8sblq3jr8b">
                <div className="flex items-center" data-id="element-5428" data-mp="1pxqf2wdkg9kv">
                  <CalendarIcon className={`h-4 w-4 mr-2 ${dk.textFaint}`} data-id="element-5429" data-mp="1uwl80r58zeip" />
                  <span data-id="element-5430" data-mp="1uugt7e5t8v3n">
                    {selectedTimeframe === '7days' && 'Last 7 days'}
                    {selectedTimeframe === '30days' && 'Last 30 days'}
                    {selectedTimeframe === '90days' && 'Last 90 days'}
                    {selectedTimeframe === 'alltime' && 'All time'}
                  </span>
                </div>
                <ChevronDownIcon className={`h-4 w-4 ml-2 ${dk.textFaint}`} data-id="element-5431" data-mp="2dnul1ae88lf1" />
              </button>
              {showTimeframeDropdown && <div className={`absolute z-10 mt-1 w-full shadow-lg rounded-md py-1 text-sm ring-1 ring-black ring-opacity-5 border ${dk.card}`} data-id="element-5432" data-mp="2ijjm5i5waadg">
                  {[{
                id: '7days',
                label: 'Last 7 days'
              }, {
                id: '30days',
                label: 'Last 30 days'
              }, {
                id: '90days',
                label: 'Last 90 days'
              }, {
                id: 'alltime',
                label: 'All time'
              }].map(tf => <button key={tf.id} className={`flex items-center w-full px-4 py-2 text-sm text-left ${selectedTimeframe === tf.id ? `${rt.bgMedium} ${rt.textPrimary}` : `${dk.textSecondary} ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}`} onClick={() => {
                setSelectedTimeframe(tf.id);
                setShowTimeframeDropdown(false);
              }} data-id="element-5433" data-mp="3gf5fwl5r89ox">
                      {tf.label}
                    </button>)}
                </div>}
            </div>

            <button className={`flex items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium ${dk.card}`} data-id="element-5434" data-mp="25tqhh5eahi7y">
              <FilterIcon className={`h-4 w-4 mr-2 ${dk.textFaint}`} data-id="element-5435" data-mp="2fqmsbhrnugfo" />
              More Filters
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className={`mb-6 border-b ${dk.border}`} data-id="element-5436" data-mp="2tjukpbnkffyf">
          <nav className="flex space-x-8 overflow-x-auto pb-px -mb-px" data-id="element-5437" data-mp="2ojg9jkqo3pw6">
            {[{
            id: 'overview',
            icon: BarChartIcon,
            label: 'Overview'
          }, {
            id: 'engagement',
            icon: FlameIcon,
            label: 'Reader Engagement'
          }, {
            id: 'devices',
            icon: SmartphoneIcon,
            label: 'Devices & Methods'
          }, {
            id: 'advanced',
            icon: BeakerIcon,
            label: 'Advanced Analytics'
          }].map(({
            id,
            icon: Icon,
            label
          }) => <button key={id} onClick={() => setActiveTab(id)} className={`pb-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${activeTab === id ? `${rt.borderAccent} ${rt.textPrimary}` : `border-transparent ${dk.textMuted} hover:${dk.textSecondary}`}`} data-id="element-5438" data-mp="35zc1fw4123k3">
                <div className="flex items-center" data-id="element-5439" data-mp="30yxwimxq39qa">
                  <Icon className="h-5 w-5 mr-2" data-id="element-5440" data-mp="2n2t2fzmxtw19" />
                  {label}
                </div>
              </button>)}
          </nav>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-5441" data-mp="lb66rks3pcyg">
          {[{
          icon: BookOpenIcon,
          iconBg: rt.bgMedium,
          iconColor: rt.textPrimary,
          label: 'Total Downloads',
          tooltip: 'The total number of times your book has been downloaded by readers.',
          value: viewMode === 'all' ? totalStats.downloads.toLocaleString() : selectedBook.downloads.toLocaleString()
        }, {
          icon: PlayIcon,
          iconBg: 'bg-purple-100',
          iconColor: 'text-purple-600',
          label: 'Start Rate',
          tooltip: 'Percentage of people who begin reading after downloading your book.',
          value: `${viewMode === 'all' ? totalStats.startRate : selectedBook.startRate}%`
        }, {
          icon: TrendingUpIcon,
          iconBg: 'bg-pink-100',
          iconColor: 'text-pink-600',
          label: 'Average Read Depth',
          tooltip: 'How far the average reader gets through your book.',
          value: `${viewMode === 'all' ? totalStats.averageReadDepth : selectedBook.averageReadDepth}%`
        }, {
          icon: CheckIcon,
          iconBg: 'bg-green-100',
          iconColor: 'text-green-600',
          label: 'Completion Rate',
          tooltip: 'Percentage of readers who finish your entire book.',
          value: `${viewMode === 'all' ? totalStats.completionRate : selectedBook.completionRate}%`
        }].map(({
          icon: Icon,
          iconBg,
          iconColor,
          label,
          tooltip,
          value
        }) => <div key={label} className={`rounded-lg shadow-sm p-6 border ${dk.card}`} data-id="element-5442" data-mp="2li505ukea7ih">
              <div className="flex items-center" data-id="element-5443" data-mp="2qhhoykuttonp">
                <div className={`rounded-full p-3 mr-4 ${iconBg}`} data-id="element-5444" data-mp="2qjc6rqasx0nk">
                  <Icon className={`h-6 w-6 ${iconColor}`} data-id="element-5445" data-mp="2vhm2q34co5a8" />
                </div>
                <div data-id="element-5446" data-mp="30ggye5nf1obr">
                  <div className="flex items-center" data-id="element-5447" data-mp="30fxdqh6dslu1">
                    <p className={`text-sm font-medium ${dk.textMuted}`} data-id="element-5448" data-mp="10v9qko1kz51">
                      {label}
                    </p>
                    <Tooltip content={tooltip} data-id="element-5449" data-mp="3s6o8p3jpkrgj" />
                  </div>
                  <h3 className={`text-2xl font-bold ${dk.textPrimary}`} data-id="element-5450" data-mp="arpxjtgxs6sl">
                    {value}
                  </h3>
                  <p className="text-sm text-green-600 flex items-center" data-id="element-5451" data-mp="5t6cdva62z1a">
                    <ArrowUpIcon className="h-3 w-3 mr-1" data-id="element-5452" data-mp="3e7eweeymihhu" />
                    from previous period
                  </p>
                </div>
              </div>
            </div>)}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && <OverviewTab viewMode={viewMode} selectedBook={selectedBook} data-id="element-5453" data-mp="3j718gl2ptrp2" />}
        {activeTab === 'engagement' && <EngagementTab viewMode={viewMode} selectedBook={selectedBook} setSelectedBook={setSelectedBook} setViewMode={setViewMode} data-id="element-5454" data-mp="12hune7ngtm17" />}
        {activeTab === 'devices' && <DevicesTab viewMode={viewMode} selectedBook={selectedBook} setSelectedBook={setSelectedBook} setViewMode={setViewMode} data-id="element-5455" data-mp="15yqlkwyggum3" />}
        {activeTab === 'advanced' && <AdvancedTab selectedAdvancedMetric={selectedAdvancedMetric} setSelectedAdvancedMetric={setSelectedAdvancedMetric} data-id="element-5456" data-mp="110yrbwac8weq" />}
      </div>
    </div>;
};
export default Analytics;